/**
 * CR-7091: the last post-shift trend rebuild, as one line on the Roll Forward card.
 *
 * "Queued" used to be the end of the story: the toast said it and nothing ever
 * said whether a worker ran it. This line reads the rebuild's own record, so a
 * rebuild stuck in the queue, skipped for want of a worker, or failed part-way
 * is visible on the screen that caused it.
 */
import { Button } from '../../ui/button';
import { AlertTriangle, CheckCircle2, Clock, Loader2, RefreshCw, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { DemoDateRebuildRun, DemoDateRebuildStatus } from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';

interface RebuildStatusLineProps {
  status: DemoDateRebuildStatus | null;
  loading: boolean;
  /** The last status fetch failed; any status shown is the last one loaded. */
  loadFailed: boolean;
  onRefresh: () => void;
}

const LOAD_FAILED = "Couldn't load the trend rebuild status. Refresh to try again.";

interface Tone {
  icon: LucideIcon;
  className: string;
  spin?: boolean;
}

const TONES: Record<DemoDateRebuildRun['status'], Tone> = {
  queued: { icon: Clock, className: 'border-amber-200 bg-amber-50 text-amber-900' },
  running: { icon: Loader2, className: 'border-sky-200 bg-sky-50 text-sky-900', spin: true },
  success: { icon: CheckCircle2, className: 'border-emerald-200 bg-emerald-50 text-emerald-900' },
  failed: { icon: XCircle, className: 'border-rose-200 bg-rose-50 text-rose-900' },
  skipped: { icon: AlertTriangle, className: 'border-rose-200 bg-rose-50 text-rose-900' },
};

const SKIP_REASONS: Record<string, string> = {
  'skipped (no worker)': 'no background worker is running',
  'skipped (queue unavailable)': 'the job queue could not be reached',
};

/**
 * One sentence saying what became of the rebuild and what it means for the
 * trend charts.
 *
 * @param run - The latest rebuild record
 * @param at - Formats a timestamp in the viewer's preferences
 * @returns The sentence to show
 */
export function describeRebuild(run: DemoDateRebuildRun, at: (iso: string) => string): string {
  const by = run.requested_by_name ? ` by ${run.requested_by_name}` : '';
  switch (run.status) {
    case 'queued':
      return run.detail
        ? `Trend rebuild waiting for another attempt after an error: ${run.detail}`
        : `Trend rebuild queued ${at(run.created_at)}${by}, waiting for a worker to pick it up.`;
    case 'running':
      return `Trend rebuild running since ${at(run.started_at ?? run.created_at)}.`;
    case 'success':
      return `Trend charts rebuilt ${at(run.finished_at ?? run.created_at)}.`;
    case 'failed':
      // Not "charts keep pre-shift months": a failed supplier-cache stage
      // fails the run even when the trend snapshots were rebuilt.
      return `Trend rebuild finished with errors: ${run.detail}.`;
    case 'skipped': {
      const reason = SKIP_REASONS[run.detail] ?? run.detail;
      // The nightly capture writes only today's rows; past months move only
      // when the snapshot backfill runs.
      return `Trend rebuild skipped ${at(run.created_at)}${by}: ${reason}. Trend charts keep pre-shift months until the snapshot backfill is run.`;
    }
  }
}

function jobsWaiting(count: number | null): string {
  if (count === null) return 'Queue count unavailable';
  return `${count} job${count === 1 ? '' : 's'} waiting in the queue`;
}

export function RebuildStatusLine({ status, loading, loadFailed, onRefresh }: RebuildStatusLineProps) {
  const { formatDateTime } = useFormatters();
  const run = status?.last_run ?? null;
  const tone = run ? TONES[run.status] : null;
  const Icon = tone?.icon;

  return (
    <div
      role="status"
      className={`flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border px-3 py-2 text-sm ${
        tone ? tone.className : 'border-border bg-muted/40 text-muted-foreground'
      }`}
    >
      <p className="flex min-w-0 flex-1 items-start gap-2">
        {Icon ? (
          <Icon aria-hidden className={`mt-0.5 h-4 w-4 shrink-0 ${tone?.spin ? 'animate-spin' : ''}`} />
        ) : null}
        <span>
          {run
            ? describeRebuild(run, formatDateTime)
            : loading
              ? 'Checking the last trend rebuild…'
              : loadFailed
                ? LOAD_FAILED
                : 'No trend rebuild has run from this screen yet.'}
        </span>
      </p>
      {run && loadFailed ? (
        <span className="text-xs font-medium">{LOAD_FAILED}</span>
      ) : status ? (
        <span className="text-xs opacity-80">{jobsWaiting(status.jobs_waiting)}</span>
      ) : null}
      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-2"
        disabled={loading}
        onClick={onRefresh}
        aria-label="Refresh trend rebuild status"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        <span className="ml-1">Refresh</span>
      </Button>
    </div>
  );
}
